/**
 * Main-thread API for ML processing.
 *
 * Tries to offload work to a Web Worker via Comlink for smooth UI.
 * Falls back to main-thread processing with yield points if Worker fails.
 */
import * as Comlink from 'comlink';
import type { MLWorkerApi, WorkerDetectionResult } from './ml-worker';
import type { DetectionResult, PoseLandmarks } from '@/types/measurements';

type WorkerProxy = Comlink.Remote<MLWorkerApi>;

let worker: Worker | null = null;
let workerProxy: WorkerProxy | null = null;
let workerFailed = false;

/**
 * Yield to the event loop so the UI can update.
 */
function yieldToUI(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Get or create the ML Worker proxy.
 * Returns null if Workers are not supported or previously failed.
 */
function getWorkerProxy(): WorkerProxy | null {
  if (workerFailed) return null;

  if (workerProxy) return workerProxy;

  try {
    worker = new Worker(new URL('./ml-worker.ts', import.meta.url), {
      type: 'module',
    });

    worker.onerror = () => {
      workerFailed = true;
      workerProxy = null;
    };

    workerProxy = Comlink.wrap<MLWorkerApi>(worker);
    return workerProxy;
  } catch {
    workerFailed = true;
    return null;
  }
}

/**
 * Process a photo using ML models.
 * Tries Worker first, falls back to main thread with yield points.
 *
 * @param imageElement - The photo to process (from camera or file upload)
 * @returns DetectionResult with landmarks and optional segmentation mask
 */
export async function processPhotoSmart(
  imageElement: HTMLImageElement
): Promise<DetectionResult> {
  const proxy = getWorkerProxy();

  if (proxy) {
    try {
      return await processInWorker(proxy, imageElement);
    } catch {
      // Worker failed — fall back to main thread
      workerFailed = true;
      workerProxy = null;
    }
  }

  return processOnMainThread(imageElement);
}

/** Race a promise against a timeout. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms / 1000}s`)),
      ms
    );
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (e) => { clearTimeout(timer); reject(e); }
    );
  });
}

const INIT_TIMEOUT_MS = 60_000; // 60 seconds per attempt (models are ~13MB, allow for slow connections)
const MAX_RETRIES = 2;

/**
 * Pre-initialize ML models. Call during loading animation.
 * Retries up to 2 times with a 30-second timeout per attempt.
 */
export async function initMLModels(): Promise<void> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      await withTimeout(initMLModelsOnce(), INIT_TIMEOUT_MS, 'ML model init');
      return; // success
    } catch (err) {
      lastError = err;
      // Reset worker state so next attempt starts fresh
      workerFailed = false;
      workerProxy = null;
      if (worker) {
        worker.terminate();
        worker = null;
      }
    }
  }

  throw new Error(
    `Failed to load AI models after ${MAX_RETRIES} attempts. ${lastError instanceof Error ? lastError.message : 'Unknown error'}`
  );
}

/** Single init attempt — tries Worker, then main thread. */
async function initMLModelsOnce(): Promise<void> {
  const proxy = getWorkerProxy();

  if (proxy) {
    try {
      await proxy.init();
      return;
    } catch {
      workerFailed = true;
      workerProxy = null;
    }
  }

  // Main-thread fallback: lazy-import and init
  // Pose is required; segmentation is optional (graceful degradation)
  const { initPoseDetector } = await import('./pose-detector');
  await initPoseDetector();

  // Pre-warm segmentation in background — don't block on failure
  import('./body-segmentation')
    .then((mod) => mod.initSegmentation())
    .catch(() => {
      /* segmentation is optional */
    });
}

/**
 * Clean up Worker and free memory.
 */
export function terminateMLWorker(): void {
  if (workerProxy) {
    workerProxy.dispose();
    workerProxy[Comlink.releaseProxy]();
    workerProxy = null;
  }
  if (worker) {
    worker.terminate();
    worker = null;
  }
}

// ─── Worker Path ───────────────────────────────────────────

async function processInWorker(
  proxy: WorkerProxy,
  imageElement: HTMLImageElement
): Promise<DetectionResult> {
  const imageBitmap = await createImageBitmap(imageElement);

  const result: WorkerDetectionResult = await proxy.processPhoto(
    Comlink.transfer(imageBitmap, [imageBitmap])
  );

  const landmarks: PoseLandmarks | null = result.landmarks
    ? (result.landmarks as PoseLandmarks)
    : null;

  // Reconstruct ImageData from transferred ArrayBuffer
  let segmentationMask: ImageData | null = null;
  if (result.maskBuffer && result.maskWidth > 0 && result.maskHeight > 0) {
    segmentationMask = new ImageData(
      new Uint8ClampedArray(result.maskBuffer),
      result.maskWidth,
      result.maskHeight
    );
  }

  // If Worker didn't produce a mask, try segmentation on main thread
  if (!segmentationMask && landmarks) {
    segmentationMask = await runMainThreadSegmentation(imageElement);
  }

  return {
    landmarks: landmarks ?? [],
    segmentationMask,
    imageWidth: result.imageWidth,
    imageHeight: result.imageHeight,
  };
}

// ─── Main-Thread Fallback ──────────────────────────────────

async function processOnMainThread(
  imageElement: HTMLImageElement
): Promise<DetectionResult> {
  const { detectPose } = await import('./pose-detector');

  await yieldToUI();
  const landmarks = await detectPose(imageElement);

  await yieldToUI();
  const segmentationMask = await runMainThreadSegmentation(imageElement);

  const width = imageElement.naturalWidth;
  const height = imageElement.naturalHeight;

  return {
    landmarks: landmarks ?? [],
    segmentationMask,
    imageWidth: width,
    imageHeight: height,
  };
}

async function runMainThreadSegmentation(
  imageElement: HTMLImageElement
): Promise<ImageData | null> {
  try {
    const { segmentBody } = await import('./body-segmentation');
    await yieldToUI();
    return await segmentBody(imageElement);
  } catch {
    // Segmentation is optional; measurement engine handles null masks
    return null;
  }
}
